import { useRef, useState } from 'react';

import { scopedScan, type ScannableGarment } from '@core/scan';

/**
 * The scan input on a counter, scoped to the booking that is open.
 *
 * <p>
 * <strong>Both counters have been headed "Scan each garment out / in" since they were built, and
 * nothing scanned.</strong> So were four printed documents: the customer copy prints a QR of the
 * booking reference under "Scan for status &amp; return date", and the docket prints one of the unit
 * serial saying "Scan the QR to close the job." The codes were correct and there was no input in
 * the product that could read one — this codebase's signature defect, in its fourth-largest
 * instance.
 * </p>
 *
 * <p>
 * <strong>A hardware scanner is a keyboard</strong>, so this is a text box in a `<form>` and Enter
 * is the submit. Nothing else is needed and nothing else is installed: no camera, no permission
 * prompt, no `BarcodeDetector`, which is Chromium-only and belongs to the later phone phase.
 * </p>
 *
 * <p>
 * <strong>It clears itself and keeps focus,</strong> because the second scan follows the first
 * within a second or two and a clerk holding a suit in one hand is not going to click back into the
 * box. The answer stays visible until the next scan replaces it.
 * </p>
 */
export function ScanBox<T extends ScannableGarment>({
  garments,
  eligible,
  onMatch,
  ineligibleHint,
  placeholder = 'Scan a garment tag',
  disabled = false,
}: {
  /** Every garment on this booking, including the ones this counter cannot act on. */
  garments: T[];
  /** Whether this counter can act on the garment. */
  eligible: (garment: T) => boolean;
  onMatch: (garment: T) => void;
  /** What to say about a garment that is on the booking but not for this counter. */
  ineligibleHint: (garment: T) => string;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <ScanInput
      placeholder={placeholder}
      disabled={disabled}
      onScan={(text) => {
        const result = scopedScan(text, garments, eligible);

        if (result.outcome === 'empty') {
          return null;
        }

        if (result.outcome === 'matched') {
          onMatch(result.garment);

          return { tone: 'good', message: `${result.garment.itemName} — ticked.` };
        }

        if (result.outcome === 'ineligible') {
          return { tone: 'bad', message: ineligibleHint(result.garment) };
        }

        /*
          Named, not a bare "not found".

          This refusal is the reason the feature is worth building: it is how a clerk catches
          themselves scanning the next booking's sherwani off the same rail, and the code has to be
          printed back for them to compare against the tag in their hand.
        */
        return { tone: 'bad', message: `${result.term} is not on this booking.` };
      }}
    />
  );
}

/** What a scan said — the line under the box until the next scan replaces it. */
export interface ScanAnswer {
  tone: 'good' | 'bad';
  message: string;
}

/**
 * The input half of {@link ScanBox}: a box that reads a scan on Enter, clears itself, keeps focus,
 * and prints what the caller says about it. The booking rail uses it directly, because what a scan
 * means there — add this garment — is a different sentence from a counter's.
 */
export function ScanInput({
  placeholder,
  disabled = false,
  onScan,
}: {
  placeholder: string;
  disabled?: boolean;
  /** Null says nothing, which is what an empty Enter should do. */
  onScan: (text: string) => ScanAnswer | null;
}) {
  const [text, setText] = useState('');
  const [said, setSaid] = useState<ScanAnswer | null>(null);
  const box = useRef<HTMLInputElement>(null);

  const submit = () => {
    const answer = onScan(text);

    if (answer === null) {
      return;
    }

    setSaid(answer);
    setText('');
    box.current?.focus();
  };

  return (
    <div className="w-full sm:w-80">
      {/*
        `data-enter-key-owns`, and it is the whole reason a scan works at all.

        Both counters put `enterKeyScope` on their wrapper, so Enter is captured and moves focus to
        the next control — which for a scan meant the payload sat in the box, no lookup ran, and the
        cursor jumped to the first wearer's tick box. Ownership is declared rather than inferred
        here (see `ENTER_OWNER_ATTRIBUTE`): calling `preventDefault` is not enough, because the
        navigation handler runs on the *capture* phase and has already acted.

        Found by scanning into a real counter. Nothing in a test could have seen it — the two
        handlers are correct in isolation and only disagree once both are on the page.
      */}
      <form
        data-enter-key-owns
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="relative">
          <i className="icon icon-scan-line text-default pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-[14px]" />
          <input
            ref={box}
            value={text}
            disabled={disabled}
            onChange={(event) => setText(event.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            className="border-border-color h-9 w-full rounded-md border bg-white ps-9 pe-3 text-[13px] text-gray-900 disabled:opacity-50"
          />
        </div>
      </form>

      {said && (
        /*
          `aria-live`, because the box clears itself and the answer is the only trace a scan
          happened. Polite rather than assertive: a run of scans is a stream of these, and assertive
          would interrupt the reader on every one.
        */
        <p
          aria-live="polite"
          className={`mt-1.5 text-[12px] ${said.tone === 'good' ? 'text-success' : 'text-danger'}`}
        >
          {said.message}
        </p>
      )}
    </div>
  );
}
